import { useEffect, useState } from 'react'

import type { Till, Vault } from '@/api/teller-types'
import { MoneyDisplay } from '@/components/money-display'
import { MoneyInput } from '@/components/money-input'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import type { Pesewas } from '@/lib/money'

import {
  MOVEABLE_CASH_KINDS,
  movementKindHint,
  movementKindLabel,
  movementNeedsTill,
  type MoveableCashKind,
} from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import { useMoveCash } from './mutations'
import { TELLER_PERMISSIONS, useTellerPermissions } from './permissions'
import { TellerActionDialog } from './teller-action-dialog'

/**
 * Moving cash between the vault, a drawer, and the bank.
 *
 * Only the four vault movements are posted here. `CustomerCashIn`, `CustomerCashOut` and
 * `OverShortAdjustment` appear on a drawer's movement list but are written by the deposit,
 * withdrawal, or close that causes them, and are refused here as `teller.movement_kind_invalid`.
 *
 * The important thing this dialog has to be honest about is *when* the cash checks happen. The
 * approval gate is asked before any balance is touched: under the shipped policy every movement
 * defers, so `teller.vault_insufficient_cash`, `teller.till_insufficient_cash` and
 * `teller.till_cash_limit_exceeded` cannot arrive in this form at all. They are raised against the
 * approver, hours later, when the countersignature re-validates the request against the cash as it
 * is then. A form that promised to catch them here would be promising something it never sees.
 */
export function CashMovementDialog({
  branchId,
  vaults,
  tills,
  open,
  onOpenChange,
  initialKind = 'VaultToTill',
  initialTillId = null,
}: {
  branchId: string
  vaults: Vault[]
  tills: Till[]
  open: boolean
  onOpenChange: (open: boolean) => void
  initialKind?: MoveableCashKind
  initialTillId?: string | null
}) {
  const permissions = useTellerPermissions()
  const [kind, setKind] = useState<MoveableCashKind>(initialKind)
  const [vaultId, setVaultId] = useState<string>('')
  const [tillId, setTillId] = useState<string>(initialTillId ?? '')
  const [amount, setAmount] = useState<Pesewas | null>(null)
  const [narration, setNarration] = useState('')

  const move = useMoveCash()

  useEffect(() => {
    if (open) {
      setKind(initialKind)
      setTillId(initialTillId ?? '')
      setVaultId(vaults.length === 1 ? vaults[0].id : '')
      setAmount(null)
      setNarration('')
    } else {
      move.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialKind, initialTillId, vaults.length])

  const needsTill = movementNeedsTill(kind)
  const selectedTill = tills.find((till) => till.id === tillId) ?? null
  const selectedVault = vaults.find((vault) => vault.id === vaultId) ?? null

  const incomplete = amount === null || amount <= 0 || (needsTill && tillId === '')

  return (
    <TellerActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Move cash"
      description="Between the branch vault, a drawer, and the institution's bank account."
      confirmLabel="Submit the movement"
      isPending={move.isPending}
      disabled={incomplete}
      error={move.isError ? move.error : null}
      notice={
        <DualControlNotice
          action="Moving vault cash"
          approvePermission={TELLER_PERMISSIONS.vaultTransferApprove}
          canCountersign={permissions.canApproveVaultTransfer}
        >
          {' '}
          Handing cash to a teller is the control a vault exists for.
        </DualControlNotice>
      }
      outcome={
        move.data ? (
          <DualControlOutcome
            result={move.data}
            awaitingDescription="The movement is queued. No cash has left the vault or the drawer."
            appliedDescription="The cash has moved and the journal entry is posted."
            deferredWarning={
              <span className="text-xs">
                The cash checks run when it is countersigned, not now: a vault or drawer that cannot
                cover this, or a drawer that would go over its holding limit, is refused then — to
                the approver, not to you.
              </span>
            }
          />
        ) : undefined
      }
      onConfirm={() => {
        if (amount === null) {
          return
        }

        move.mutate({
          kind,
          branchId,
          // Null asks the server for the branch's own vault, which is what a single-vault branch
          // wants and what a caller who cannot read the vault list has to send.
          vaultId: vaultId === '' ? null : vaultId,
          tillId: needsTill ? (tillId === '' ? null : tillId) : null,
          amountMinor: amount,
          currencyCode: null,
          narration: narration.trim() === '' ? null : narration.trim(),
        })
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="movement-kind">Movement</Label>
        <Select value={kind} onValueChange={(value) => setKind(value as MoveableCashKind)}>
          <SelectTrigger id="movement-kind">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MOVEABLE_CASH_KINDS.map((value) => (
              <SelectItem key={value} value={value}>
                {movementKindLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted">{movementKindHint(kind)}</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="movement-vault">Vault</Label>
        <Select
          value={vaultId === '' ? undefined : vaultId}
          onValueChange={setVaultId}
          disabled={vaults.length === 0}
        >
          <SelectTrigger id="movement-vault">
            <SelectValue placeholder={vaults.length === 0 ? 'The branch vault' : 'Choose a vault'} />
          </SelectTrigger>
          <SelectContent>
            {vaults.map((vault) => (
              <SelectItem key={vault.id} value={vault.id}>
                {vault.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted">
          {vaults.length === 0
            ? `Vault balances need ${TELLER_PERMISSIONS.vaultView}, which your role does not hold. Leaving this unset sends the movement against the branch's own vault, which is what a single-vault branch has.`
            : selectedVault
              ? 'The vault this movement is against.'
              : 'Leave unset to use the branch’s own vault.'}
        </p>
        {selectedVault ? (
          <p className="text-xs text-body">
            Holding{' '}
            <MoneyDisplay
              value={selectedVault.cashOnHandMinor}
              currency={selectedVault.currencyCode}
              className="inline-block text-left"
            />
            .
          </p>
        ) : null}
      </div>

      {needsTill ? (
        <div className="space-y-1.5">
          <Label htmlFor="movement-till">Till</Label>
          <Select value={tillId === '' ? undefined : tillId} onValueChange={setTillId}>
            <SelectTrigger id="movement-till">
              <SelectValue placeholder="Choose a drawer" />
            </SelectTrigger>
            <SelectContent>
              {tills.map((till) => (
                <SelectItem key={till.id} value={till.id}>
                  {till.tillNumber} · {till.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedTill ? (
            <p className="text-xs text-body">
              Holding{' '}
              <MoneyDisplay
                value={selectedTill.cashOnHandMinor}
                currency={selectedTill.currencyCode}
                className="inline-block text-left"
              />
              {selectedTill.cashHoldingLimitMinor === null ? (
                <>, with no holding limit set.</>
              ) : (
                <>
                  {' '}
                  against a limit of{' '}
                  <MoneyDisplay
                    value={selectedTill.cashHoldingLimitMinor}
                    currency={selectedTill.currencyCode}
                    className="inline-block text-left"
                  />
                  .
                </>
              )}
            </p>
          ) : (
            <p className="text-xs text-muted">
              A float and a banking both name the drawer they concern.
            </p>
          )}
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="movement-amount">Amount</Label>
        <MoneyInput id="movement-amount" value={amount} onChange={setAmount} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="movement-narration">Narration</Label>
        <Input
          id="movement-narration"
          value={narration}
          onChange={(event) => setNarration(event.target.value)}
          autoComplete="off"
          placeholder="Why the cash is moving"
        />
      </div>

      <Alert tone="info">
        <div className="space-y-1">
          <AlertTitle>The cash checks happen at countersignature</AlertTitle>
          <AlertDescription>
            This request is submitted for approval before any balance is read, so a vault or drawer
            that cannot cover it — and a drawer that would go over its holding limit — is refused
            when the movement is countersigned, against the cash as it stands then.
          </AlertDescription>
        </div>
      </Alert>
    </TellerActionDialog>
  )
}
